import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, RotateCcw, ShoppingBag, Tag, TrendingDown, Undo2, Users } from 'lucide-react';
import type {
  MoneyAmount,
  SalesBreakdownRow,
  SalesDrilldownKind,
  SalesProductRow,
  SalesReport,
  SalesReportQuery,
} from '@optiflow/types';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { ChangeText } from './ChangeText';
import { MoneyByCurrencyText } from './MoneyByCurrencyText';
import { SalesRecords } from './SalesRecords';

const NetSalesTrendChart = lazy(() => import('./NetSalesTrendChart'));
const SalesBreakdownChart = lazy(() => import('./SalesBreakdownChart'));

/** The base-currency percentage of a per-currency change, which is what a single figure can show. */
function baseChange(
  change: { currencyCode: string; pctBasisPoints: number | null }[] | null,
  base: string,
) {
  if (change === null) return undefined;
  return change.find((entry) => entry.currencyCode === base)?.pctBasisPoints ?? null;
}

function Figure({
  icon,
  label,
  amounts,
  count,
  change,
  invert,
  hint,
  onOpen,
  unavailable,
}: {
  icon: React.ReactNode;
  label: string;
  amounts?: MoneyAmount[];
  count?: number;
  change?: number | null;
  invert?: boolean;
  hint?: string;
  onOpen?: () => void;
  /** Shown instead of a number when the figure does not exist, such as an average of no orders. */
  unavailable?: string;
}) {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-muted text-sm">{label}</p>
        <span className="bg-brand-50 text-brand-700 rounded-lg p-2">{icon}</span>
      </div>
      <div className="mt-3">
        {unavailable ? (
          <p className="text-muted text-lg font-medium">{unavailable}</p>
        ) : amounts ? (
          <MoneyByCurrencyText amounts={amounts} className="text-ink text-2xl font-semibold" />
        ) : (
          <p className="text-ink text-2xl font-semibold" dir="ltr">
            {count ?? 0}
          </p>
        )}
      </div>
      {amounts && amounts.length > 1 ? (
        <p className="mt-1 text-xs text-amber-700">{t('reports.mixedCurrencies')}</p>
      ) : null}
      <div className="mt-2">
        <ChangeText basisPoints={change} invert={invert} />
      </div>
      {hint ? <p className="text-muted mt-1 text-xs">{hint}</p> : null}
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="text-brand-700 mt-2 text-sm font-medium hover:underline"
          aria-label={t('reports.sales.records.viewFor', { figure: label })}
        >
          {t('reports.sales.records.view')}
        </button>
      ) : null}
    </Card>
  );
}

/** A breakdown as a table. Charts illustrate; this is what a reader checks a number against. */
function BreakdownTable({
  title,
  subtitle,
  rows,
  emptyLabel,
  unnamedLabel,
  countOnly,
}: {
  title: string;
  subtitle?: string;
  rows: SalesBreakdownRow[];
  emptyLabel: string;
  /** For a row that has an id but no name, such as an operator whose user row no longer exists. */
  unnamedLabel?: string;
  /** A count per row and no money: `confirmedOrders` then holds that count, as for returns by branch. */
  countOnly?: { label: string };
}) {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-ink font-semibold">{title}</h3>
        {subtitle ? <p className="text-muted text-xs">{subtitle}</p> : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-muted mt-3 text-sm">{emptyLabel}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted border-line border-b text-start">
                <th className="py-2 text-start font-medium">{t('reports.sales.columns.name')}</th>
                <th className="py-2 text-end font-medium">
                  {countOnly ? countOnly.label : t('reports.sales.columns.orders')}
                </th>
                {countOnly ? null : (
                  <th className="py-2 text-end font-medium">
                    {t('reports.sales.columns.netSales')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id ?? 'unattributed'} className="border-line border-b last:border-b-0">
                  <td className="py-2">
                    {row.label ?? (
                      <span className="text-muted italic">
                        {row.id !== null && unnamedLabel
                          ? unnamedLabel
                          : t('reports.sales.unattributed')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-end tabular-nums" dir="ltr">
                    {row.confirmedOrders}
                  </td>
                  {countOnly ? null : (
                    <td className="py-2 text-end">
                      <MoneyByCurrencyText amounts={row.netSales} className="justify-end" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ProductTable({
  title,
  subtitle,
  rows,
  quantityOnly,
}: {
  title: string;
  subtitle?: string;
  rows: SalesProductRow[];
  quantityOnly?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-ink font-semibold">{title}</h3>
        {subtitle ? <p className="text-muted text-xs">{subtitle}</p> : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-muted mt-3 text-sm">{t('reports.sales.empty')}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted border-line border-b">
                <th className="py-2 text-start font-medium">
                  {t('reports.sales.columns.product')}
                </th>
                <th className="py-2 text-end font-medium">{t('reports.sales.columns.quantity')}</th>
                {quantityOnly ? null : (
                  <th className="py-2 text-end font-medium">
                    {t('reports.sales.columns.netSales')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.variantId ?? 'unknown'}-${row.rank}`}
                  className="border-line border-b last:border-b-0"
                >
                  <td className="py-2">
                    <span className="text-ink">{row.name ?? t('reports.sales.unattributed')}</span>
                    {row.sku ? (
                      <span className="text-muted ms-2 text-xs" dir="ltr">
                        {row.sku}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 text-end tabular-nums" dir="ltr">
                    {row.quantity}
                  </td>
                  {quantityOnly ? null : (
                    <td className="py-2 text-end">
                      <MoneyByCurrencyText amounts={row.netSales} className="justify-end" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function SalesSkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="bg-surface border-line h-28 animate-pulse rounded-xl border"
          />
        ))}
      </div>
      <div className="bg-surface border-line h-64 animate-pulse rounded-xl border" />
    </div>
  );
}

/**
 * The Sales tab.
 *
 * Every figure here is the server's, and the five at the top are the same ones the Overview shows —
 * this page breaks them down, it does not recompute them. Where a row has no name, the sale is
 * still shown under an explicit unattributed label rather than dropped, so a breakdown always adds
 * up to the figure above it.
 */
export function SalesContent({ report, query }: { report: SalesReport; query: SalesReportQuery }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [showTrendTable, setShowTrendTable] = useState(false);
  const [openRecords, setOpenRecords] = useState<SalesDrilldownKind | null>(null);
  const base = report.meta.baseCurrencyCode;
  const { headline } = report;

  const aov = headline.averageOrderValue.current.map((entry) => ({
    currencyCode: entry.currencyCode,
    amountMinor: entry.amountMinor ?? 0,
  }));
  const hasAverage = headline.averageOrderValue.current.some((entry) => entry.amountMinor !== null);
  const trendSeries = report.trend.series.find((entry) => entry.currencyCode === base);

  return (
    <div className="space-y-5">
      <section aria-labelledby="sales-figures" className="space-y-3">
        <h2 id="sales-figures" className="text-ink font-semibold">
          {t('reports.sales.sections.figures')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Figure
            icon={<Banknote className="h-4 w-4" />}
            label={t('reports.kpis.netSales')}
            amounts={headline.netSales.current}
            change={baseChange(headline.netSales.change, base)}
          />
          <Figure
            icon={<ShoppingBag className="h-4 w-4" />}
            label={t('reports.sales.kpis.confirmedSales')}
            amounts={headline.confirmedSales.current}
            change={baseChange(headline.confirmedSales.change, base)}
          />
          <Figure
            icon={<ShoppingBag className="h-4 w-4" />}
            label={t('reports.kpis.confirmedOrders')}
            count={headline.confirmedOrders.current}
            change={headline.confirmedOrders.change?.pctBasisPoints ?? null}
            onOpen={() => setOpenRecords('CONFIRMED_ORDERS')}
          />
          <Figure
            icon={<Undo2 className="h-4 w-4" />}
            label={t('reports.sales.kpis.returns')}
            amounts={headline.returns.current}
            change={baseChange(headline.returns.change, base)}
            invert
            hint={t('reports.sales.hints.returnsMoney')}
            onOpen={() => setOpenRecords('RETURNS')}
          />
          <Figure
            icon={<RotateCcw className="h-4 w-4" />}
            label={t('reports.sales.kpis.cancellations')}
            amounts={headline.cancellations.current}
            change={baseChange(headline.cancellations.change, base)}
            invert
            hint={t('reports.sales.hints.cancellations')}
            onOpen={() => setOpenRecords('CANCELLATIONS')}
          />
          <Figure
            icon={<Tag className="h-4 w-4" />}
            label={t('reports.sales.kpis.averageOrderValue')}
            amounts={aov}
            unavailable={hasAverage ? undefined : t('reports.sales.noAverage')}
            change={baseChange(headline.averageOrderValue.change, base)}
            hint={t('reports.sales.hints.aov')}
          />
        </div>
        {openRecords ? (
          <SalesRecords
            key={openRecords}
            query={query}
            kind={openRecords}
            onClose={() => setOpenRecords(null)}
          />
        ) : null}
      </section>

      <Card className="p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h3 className="text-ink font-semibold">{t('reports.trend.title')}</h3>
            <p className="text-muted text-xs">
              {t(`reports.granularity.${report.trend.granularity}`)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowTrendTable((shown) => !shown)}
            className="text-brand-700 text-sm font-medium hover:underline"
          >
            {showTrendTable ? t('reports.trend.viewChart') : t('reports.trend.viewTable')}
          </button>
        </div>
        {showTrendTable ? (
          <div className="mt-3 max-h-72 overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted border-line border-b">
                  <th className="py-2 text-start font-medium">{t('reports.trend.period')}</th>
                  <th className="py-2 text-end font-medium">{t('reports.trend.current')}</th>
                  <th className="py-2 text-end font-medium">{t('reports.trend.previous')}</th>
                </tr>
              </thead>
              <tbody>
                {report.trend.buckets.map((bucket, index) => (
                  <tr key={bucket.from} className="border-line border-b last:border-b-0">
                    <td className="py-2" dir="ltr">
                      {bucket.from === bucket.to ? bucket.from : `${bucket.from} – ${bucket.to}`}
                    </td>
                    <td className="py-2 text-end" dir="ltr">
                      {formatMoney(trendSeries?.current[index] ?? 0, locale, base)}
                    </td>
                    <td className="text-muted py-2 text-end" dir="ltr">
                      {trendSeries?.previous?.[index] === null ||
                      trendSeries?.previous?.[index] === undefined
                        ? '—'
                        : formatMoney(trendSeries.previous[index] as number, locale, base)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Suspense fallback={<div className="h-56" aria-hidden />}>
            <NetSalesTrendChart trend={report.trend} baseCurrencyCode={base} />
          </Suspense>
        )}
      </Card>

      <section aria-labelledby="sales-breakdowns" className="space-y-3">
        <h2 id="sales-breakdowns" className="text-ink font-semibold">
          {t('reports.sales.sections.breakdowns')}
        </h2>
        {report.byBranch.length > 1 ? (
          <Card className="p-4">
            <h3 className="text-ink font-semibold">{t('reports.sales.byBranch')}</h3>
            <Suspense fallback={<div className="h-48" aria-hidden />}>
              <SalesBreakdownChart rows={report.byBranch} baseCurrencyCode={base} />
            </Suspense>
          </Card>
        ) : null}
        <div className="grid gap-4 xl:grid-cols-2">
          <BreakdownTable
            title={t('reports.sales.byBranch')}
            rows={report.byBranch}
            emptyLabel={t('reports.sales.empty')}
          />
          <BreakdownTable
            title={t('reports.sales.byOperator')}
            subtitle={t('reports.sales.hints.operator')}
            rows={report.byOperator}
            emptyLabel={t('reports.sales.empty')}
            unnamedLabel={t('reports.sales.records.unknownOperator')}
          />
          <BreakdownTable
            title={t('reports.sales.byCategory')}
            subtitle={t('reports.sales.hints.currentCatalogue')}
            rows={report.byCategory}
            emptyLabel={t('reports.sales.empty')}
          />
          <BreakdownTable
            title={t('reports.sales.byBrand')}
            subtitle={t('reports.sales.hints.currentCatalogue')}
            rows={report.byBrand}
            emptyLabel={t('reports.sales.empty')}
          />
        </div>
      </section>

      <section aria-labelledby="sales-products" className="space-y-3">
        <h2 id="sales-products" className="text-ink font-semibold">
          {t('reports.sales.sections.products')}
        </h2>
        <div className="grid gap-4 xl:grid-cols-2">
          <ProductTable
            title={t('reports.sales.topProducts')}
            subtitle={t('reports.sales.hints.identity')}
            rows={report.topProducts}
          />
          <ProductTable
            title={t('reports.sales.lowestProducts')}
            subtitle={t('reports.sales.hints.lowest')}
            rows={report.lowestProducts}
          />
        </div>
      </section>

      <section aria-labelledby="sales-reversals" className="space-y-3">
        <h2 id="sales-reversals" className="text-ink font-semibold">
          {t('reports.sales.sections.reversals')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Figure
            icon={<Undo2 className="h-4 w-4" />}
            label={t('reports.sales.kpis.returnsRecorded')}
            count={report.returns.recorded.current}
            change={report.returns.recorded.change?.pctBasisPoints ?? null}
            invert
            hint={t('reports.sales.hints.returnsRecorded')}
          />
          <Figure
            icon={<Undo2 className="h-4 w-4" />}
            label={t('reports.sales.kpis.returnedQuantity')}
            count={report.returns.quantity.current}
            change={report.returns.quantity.change?.pctBasisPoints ?? null}
            invert
          />
          <Figure
            icon={<RotateCcw className="h-4 w-4" />}
            label={t('reports.sales.kpis.reversedOrders')}
            count={report.cancellations.reversedOrders.current}
            change={report.cancellations.reversedOrders.change?.pctBasisPoints ?? null}
            invert
            hint={t('reports.sales.hints.reversedOrders')}
          />
          <Figure
            icon={<TrendingDown className="h-4 w-4" />}
            label={t('reports.sales.kpis.beforeConfirmation')}
            count={report.cancellations.beforeConfirmationOrders.current}
            change={report.cancellations.beforeConfirmationOrders.change?.pctBasisPoints ?? null}
            hint={t('reports.sales.hints.beforeConfirmation')}
          />
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          <ProductTable
            title={t('reports.sales.topReturned')}
            subtitle={t('reports.sales.hints.returnsRecorded')}
            rows={report.returns.topProducts}
            quantityOnly
          />
          <BreakdownTable
            title={t('reports.sales.returnsByBranch')}
            subtitle={t('reports.sales.hints.returnsRecorded')}
            rows={report.returns.byBranch}
            emptyLabel={t('reports.sales.noReturns')}
            countOnly={{ label: t('reports.sales.kpis.returnsRecorded') }}
          />
        </div>
      </section>

      <p className="text-muted flex items-center gap-2 text-xs">
        <Users className="h-3.5 w-3.5" />
        {t('reports.sales.disclosure')}
      </p>
    </div>
  );
}
